import React, { useState, useEffect } from 'react';
import apiClient from '../api/client';

interface Alert {
  id: string;
  alert_type: string;
  message: string;
  timestamp: string;
  delivered: boolean;
}

interface PaginatedAlerts {
  alerts: Alert[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export function EmployeeAlerts() {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 10;

  useEffect(() => {
    const fetchAlerts = async () => {
      setLoading(true);
      try {
        const response = await apiClient.get<PaginatedAlerts>('/api/employee/alerts', {
          params: { page, page_size: pageSize }
        });
        setAlerts(response.data.alerts);
        setTotalPages(response.data.total_pages);
        setTotal(response.data.total);
        setError('');
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to load alerts');
      } finally {
        setLoading(false);
      }
    };
    fetchAlerts();
  }, [page]);

  const getAlertConfig = (type: string) => {
    switch (type) {
      case 'break_reminder':
        return { 
          icon: '⏰', 
          color: '#F59E0B', 
          bg: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
          label: 'Break Reminder',
          tip: 'Time to stretch!'
        };
      case 'posture_correction':
        return { 
          icon: '🧘', 
          color: '#3B82F6', 
          bg: 'linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%)',
          label: 'Posture Tip',
          tip: 'Sit up straight'
        };
      case 'hydration_reminder':
        return { 
          icon: '💧', 
          color: '#06B6D4', 
          bg: 'linear-gradient(135deg, #CFFAFE 0%, #A5F3FC 100%)',
          label: 'Hydration',
          tip: 'Drink some water'
        };
      case 'cold_detection':
        return { 
          icon: '🧣', 
          color: '#8B5CF6', 
          bg: 'linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)',
          label: 'Comfort Alert',
          tip: 'Stay warm'
        };
      default:
        return { 
          icon: '📢', 
          color: '#6B7280', 
          bg: 'linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%)',
          label: 'Notification',
          tip: ''
        };
    }
  };

  const formatTimeAgo = (timestamp: string): string => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const formatFullDate = (timestamp: string): string => {
    return new Date(timestamp).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && alerts.length === 0) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingSpinner} />
        <p style={styles.loadingText}>Loading your wellness nudges...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <h1 style={styles.title}>Wellness Nudges</h1>
          <p style={styles.subtitle}>Your personalized wellness reminders and tips</p>
        </div>
        <div style={styles.headerStats}>
          <div style={styles.statBadge}>
            <span style={styles.statValue}>{total}</span>
            <span style={styles.statLabel}>Total Nudges</span>
          </div>
        </div>
      </div>

      {error && (
        <div style={styles.errorBox}>
          <span>⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {alerts.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>🎉</div>
          <h3 style={styles.emptyTitle}>All caught up!</h3>
          <p style={styles.emptyText}>No wellness nudges yet. Keep up the great work!</p>
          <div style={styles.emptyTips}>
            <div style={styles.tipCard}>
              <span>💡</span>
              <span>Take regular breaks every 30 minutes</span>
            </div>
            <div style={styles.tipCard}>
              <span>💡</span>
              <span>Stay hydrated throughout the day</span>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div style={styles.alertList}>
            {alerts.map((alert, index) => {
              const config = getAlertConfig(alert.alert_type);
              return (
                <div 
                  key={alert.id} 
                  style={{
                    ...styles.alertCard,
                    animationDelay: `${index * 0.05}s`,
                  }}
                >
                  <div style={{...styles.alertIconWrapper, background: config.bg}}>
                    <span style={styles.alertIcon}>{config.icon}</span>
                  </div>
                  
                  <div style={styles.alertContent}>
                    <div style={styles.alertHeader}>
                      <span style={{...styles.alertType, color: config.color}}>
                        {config.label}
                      </span>
                      <span style={styles.alertTime} title={formatFullDate(alert.timestamp)}>
                        {formatTimeAgo(alert.timestamp)}
                      </span>
                    </div>
                    <p style={styles.alertMessage}>{alert.message}</p>
                    <div style={styles.alertFooter}>
                      <span style={styles.alertDate}>{formatFullDate(alert.timestamp)}</span>
                      {alert.delivered && (
                        <span style={styles.deliveredBadge}>
                          <span>✓</span> Sent via Slack
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={styles.pagination}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{
                  ...styles.pageBtn,
                  opacity: page === 1 ? 0.5 : 1,
                  cursor: page === 1 ? 'not-allowed' : 'pointer',
                }}
              >
                ← Previous
              </button>
              
              <div style={styles.pageInfo}>
                <span style={styles.pageNumber}>Page {page}</span>
                <span style={styles.pageDivider}>of</span>
                <span style={styles.pageNumber}>{totalPages}</span>
              </div>
              
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{
                  ...styles.pageBtn,
                  opacity: page === totalPages ? 0.5 : 1,
                  cursor: page === totalPages ? 'not-allowed' : 'pointer',
                }}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: { maxWidth: '900px', margin: '0 auto' },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '50vh',
    gap: '16px',
  },
  loadingSpinner: {
    width: '48px',
    height: '48px',
    border: '4px solid #E5E7EB',
    borderTopColor: '#10B981',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: { color: '#6B7280', fontSize: '15px' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '32px',
  },
  headerContent: {},
  title: {
    fontSize: '28px',
    fontWeight: 700,
    color: '#064E3B',
    margin: 0,
  },
  subtitle: {
    fontSize: '15px',
    color: '#6B7280',
    marginTop: '6px',
  },
  headerStats: {},
  statBadge: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '16px 24px',
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
  },
  statValue: { fontSize: '28px', fontWeight: 700, color: '#10B981' },
  statLabel: { fontSize: '12px', color: '#6B7280', marginTop: '2px' },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '14px 18px',
    background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)',
    borderRadius: '12px',
    color: '#DC2626',
    fontSize: '14px',
    marginBottom: '20px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '60px 20px',
    background: 'white',
    borderRadius: '24px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
    textAlign: 'center',
  },
  emptyIcon: { fontSize: '64px', marginBottom: '16px' },
  emptyTitle: { fontSize: '22px', fontWeight: 600, color: '#1F2937', margin: 0 },
  emptyText: { fontSize: '15px', color: '#6B7280', marginTop: '8px' },
  emptyTips: {
    display: 'flex',
    gap: '12px',
    marginTop: '32px',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  tipCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    background: '#F0FDF4',
    borderRadius: '10px',
    fontSize: '13px',
    color: '#065F46',
  },
  alertList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  alertCard: {
    display: 'flex',
    gap: '16px',
    padding: '20px',
    background: 'white',
    borderRadius: '20px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
    animation: 'fadeIn 0.4s ease-out forwards',
    opacity: 0,
  },
  alertIconWrapper: {
    width: '56px',
    height: '56px',
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  alertIcon: { fontSize: '26px' },
  alertContent: { flex: 1, minWidth: 0 },
  alertHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '6px',
  },
  alertType: {
    fontSize: '13px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  alertTime: {
    fontSize: '12px',
    color: '#9CA3AF',
    fontWeight: 500,
  },
  alertMessage: {
    fontSize: '15px',
    color: '#1F2937',
    margin: 0,
    lineHeight: 1.5,
  },
  alertFooter: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginTop: '10px',
  },
  alertDate: {
    fontSize: '12px',
    color: '#9CA3AF',
  },
  deliveredBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '11px',
    fontWeight: 600,
    color: '#10B981',
    background: '#D1FAE5',
    padding: '4px 10px',
    borderRadius: '6px',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '20px',
    marginTop: '32px',
    padding: '20px',
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
  },
  pageBtn: {
    padding: '10px 20px',
    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontWeight: 600,
    fontSize: '14px',
    transition: 'all 0.2s',
  },
  pageInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  pageNumber: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#1F2937',
  },
  pageDivider: {
    fontSize: '14px',
    color: '#9CA3AF',
  },
};
